import type { OrderDownloadRequestDto } from '@onrunning/backstage-models';
import axios, { HttpStatusCode, type AxiosRequestConfig, type AxiosResponse, type Method } from 'axios';
import { ValidatorResult } from '../models/validation-result.model';
import { useUserStore } from '../stores/user';

export class CrudService<T = any> {
  private apiUrl!: string;

  constructor(controller?: string) {
    if (import.meta.env.VITE_APP_onBackstageAPI) {
      this.apiUrl = import.meta.env.VITE_APP_onBackstageAPI + (controller ? controller + '/' : '');
    } else {
      throw new Error(
        'you need to define a global environment variable and add a value to the onBackstageAPI key/property',
      );
    }
  }

  public async get(id: string, config?: AxiosRequestConfig): Promise<ValidatorResult<T>> {
    const result = new ValidatorResult<T>();
    try {
      const response = await axios.get(this.apiUrl + id, config);

      this.updateJwt(response);

      result.model = response.data;
    } catch (exception) {
      this.onError(exception, result);
    }
    return result;
  }

  public async getAll(
    /**
     * (optional; default: `automatic`) How to handle errors. `throw` will
     * result in errors being thrown as-is (i.e., an axios error object with a
     * `response` property -- see
     * https://github.com/axios/axios#handling-errors). `automatic` will handle
     * errors automatically, including showing a popup with an error message
     * and a redirect to the login page if needed. `silent` will do nothing
     * with the error.
     */
    errorHandling: 'throw' | 'automatic' | 'silent' = 'automatic',
    config?: AxiosRequestConfig,
  ): Promise<ValidatorResult<T[]>> {
    const result = new ValidatorResult<T[]>();
    try {
      const response = await axios.get(this.apiUrl, config);

      this.updateJwt(response);

      result.model = response.data;
    } catch (exception) {
      if (errorHandling === 'automatic') {
        this.onError(exception, result);
      }
      if (errorHandling === 'throw') {
        throw exception;
      }
    }
    return result;
  }

  public async post(model: T, config?: AxiosRequestConfig): Promise<ValidatorResult<T>> {
    const result = new ValidatorResult<T>(model);
    try {
      const response = await axios.post(this.apiUrl, model, config);

      this.updateJwt(response);

      result.model = response.data;
    } catch (exception) {
      this.onError(exception, result);
    }
    return result;
  }

  public async put(id: any, model: T, config?: AxiosRequestConfig): Promise<ValidatorResult<T>> {
    const result = new ValidatorResult<T>(model);
    try {
      const response = await axios.put(this.apiUrl + id, model, config);

      this.updateJwt(response);
    } catch (exception) {
      this.onError(exception, result);
    }
    return result;
  }

  public async remove(id: any, config?: AxiosRequestConfig): Promise<ValidatorResult<T>> {
    const result = new ValidatorResult<T>(undefined);
    try {
      const response = await axios.delete(this.apiUrl + id, config);

      this.updateJwt(response);
    } catch (exception) {
      this.onError(exception, result);
    }
    return result;
  }

  public async do(
    method: string,
    endpoint: string,
    body?: any,
    headers?: any,

    /**
     * (optional; default: `automatic`) How to handle errors. `throw` will
     * result in errors being thrown as-is (i.e., an axios error object with a
     * `response` property -- see
     * https://github.com/axios/axios#handling-errors). `automatic` will handle
     * errors automatically, including showing a popup with an error message
     * and a redirect to the login page if needed. `silent` will do nothing
     * with the error.
     */
    errorHandling: 'throw' | 'automatic' | 'silent' = 'automatic',
  ): Promise<ValidatorResult<any>> {
    const result = new ValidatorResult<any>(undefined);
    try {
      const requestOptions: AxiosRequestConfig = {
        url: this.apiUrl + endpoint,
        method: method as Method,
      };
      if (body) {
        requestOptions.data = body;
      }
      if (headers) {
        requestOptions.headers = headers;
      }

      const response = await axios.request(requestOptions);

      this.updateJwt(response);

      result.model = response.data;
    } catch (exception) {
      if (errorHandling === 'automatic') {
        this.onError(exception, result);
      }
      if (errorHandling === 'throw') {
        throw exception;
      }
    }
    return result;
  }

  public async doWithCustomType<U>(
    method: string,
    endpoint: string,
    body?: any,
    headers?: any,

    /**
     * (optional; default: `automatic`) How to handle errors. `throw` will
     * result in errors being thrown as-is (i.e., an axios error object with a
     * `response` property -- see
     * https://github.com/axios/axios#handling-errors). `automatic` will handle
     * errors automatically, including showing a popup with an error message
     * and a redirect to the login page if needed. `silent` will do nothing
     * with the error.
     */
    errorHandling: 'throw' | 'automatic' | 'silent' = 'automatic',
  ): Promise<ValidatorResult<U>> {
    const result = new ValidatorResult<U>(undefined);
    try {
      const requestOptions: AxiosRequestConfig = {
        url: this.apiUrl + endpoint,
        method: method as Method,
      };

      if (body) {
        requestOptions.data = body;
      }

      if (headers) {
        requestOptions.headers = headers;
      }

      const response = await axios.request(requestOptions);

      this.updateJwt(response);

      //todo ww don't add errors into the results, this means results.isValid() will always be true even there is no model.
      result.model = response.data;
    } catch (exception) {
      if (errorHandling === 'automatic') {
        this.onError(exception, result);
      }

      if (errorHandling === 'throw') {
        throw exception;
      }
    }

    return result;
  }

  public async downloadFile(
    endpoint: string,
    orderDownloadRequestDto: OrderDownloadRequestDto,
    fileName?: string,
    config?: AxiosRequestConfig,
  ): Promise<void> {
    try {
      const response = await axios.post(this.apiUrl + endpoint, orderDownloadRequestDto, {
        ...config,
        responseType: 'blob',
      });

      if (response.status !== HttpStatusCode.Ok) {
        throw new Error('File download failed');
      }

      this.updateJwt(response);

      const filename =
        fileName || this.extractFilename(response.headers['content-disposition']) || 'backstage-order-download.xlsx';
      this.triggerDownload(response.data, filename);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw new Error('File download failed');
    }
  }

  private extractFilename(contentDisposition?: string): string | null {
    if (!contentDisposition) return null;
    const match = contentDisposition.match(/filename="?(.*)"?/);

    return match?.[1] || null;
  }

  private triggerDownload(data: BlobPart, filename: string): void {
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  }

  private updateJwt(response?: AxiosResponse) {
    if (response?.headers['x-jwt-cookie']) {
      localStorage.setItem('jwt', response.headers['x-jwt-cookie']);
    }
  }

  private onError(exception: any, result: ValidatorResult<any>) {
    console.info('request failed to get a model', exception);
    // let globalErrors: ValidationError[] = [];

    if (exception?.response && exception?.response?.data) {
      result.errors = exception.response.data;
    }

    // if (result.errors.length) {
    //   globalErrors = result.errors.filter((error: ValidationError) => error.errorType === 'global');
    // }

    if (exception?.response?.status === 401) {
      const { logout } = useUserStore();
      logout();
      return;
    }

    // ToDo: change to a composable?
    // const errorMsg = globalErrors?.length ? i18n.t(globalErrors[0].error) : i18n.t('global.error');

    // this.$snotify.error(errorMsg);
  }
}
